import type { BroadcastEngagementFilter } from '@/hooks/broadcasts/broadcasts-types';

/**
 * TanStack Query key factory for the broadcasts home list — the org's
 * broadcasts (drafts through sent) read directly from `public.broadcasts`.
 *
 * Multi-tenant scope is mandatory on every key: omitting `orgId` from a
 * `getQueryData`/`setQueryData`/`invalidateQueries` call silently returns
 * `undefined` and produces cross-tenant cache bugs (`.claude/rules/frontend.md`).
 *
 * The list key is flat (`list(orgId)`) — filtering by status / search happens
 * client-side in a `useMemo` (suppressions precedent), so a single cached query
 * backs every filter combination. `detail(id)` keys the per-broadcast builder
 * views; `contentVersions(id)` and `events(id)` nest UNDER that detail key so a
 * `detail`-prefixed invalidation refreshes a broadcast's version history and
 * activity timeline alongside its row.
 *
 * The org-independent `root()` prefix is what mutations invalidate: prefix
 * matching catches every org variant, so a momentarily-null current org can
 * never strand the real `[..., orgId]` query stale.
 */
export const broadcastKeys = {
  root: () => ['broadcasts'] as const,
  all: (orgId: string | null | undefined) => [...broadcastKeys.root(), orgId] as const,
  ideas: (orgId: string | null | undefined, userId: string | undefined, language: string) =>
    [...broadcastKeys.all(orgId), 'ideas', userId, language] as const,
  ideaDismissals: (orgId: string | null | undefined, userId: string | undefined) =>
    [...broadcastKeys.all(orgId), 'idea-dismissals', userId] as const,
  /** The org's full broadcast list (filtered client-side). */
  list: (orgId: string | null | undefined) => [...broadcastKeys.all(orgId), 'list'] as const,
  /** A single broadcast by id. */
  detail: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.all(orgId), 'detail', broadcastId] as const,
  /**
   * A broadcast's append-only content-version history (nested under its detail).
   * `limit` is part of the key so distinct caps cache separately, yet the key
   * stays under the `detail` prefix so a `detail`-prefixed invalidation still
   * catches every limit variant.
   */
  contentVersions: (orgId: string | null | undefined, broadcastId: string, limit: number) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'content-versions', limit] as const,
  /** A broadcast's append-only activity timeline (nested under its detail). */
  events: (
    orgId: string | null | undefined,
    broadcastId: string,
    limit: number,
    eventType?: string,
  ) =>
    eventType
      ? ([...broadcastKeys.detail(orgId, broadcastId), 'events', limit, { eventType }] as const)
      : ([...broadcastKeys.detail(orgId, broadcastId), 'events', limit] as const),
  /** Server-authoritative readiness for the latest or approved artifact. */
  preflight: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'preflight'] as const,
  /** Read-only owned-renderer output for the server-selected review artifact. */
  preview: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'preview'] as const,
  /**
   * One A/B arm's read-only render (its pinned version, arm-scoped utm).
   * Nested UNDER the preview key so `preview`- and `detail`-prefixed
   * invalidations refresh the arm renders alongside the broadcast-level one.
   */
  previewVariant: (orgId: string | null | undefined, broadcastId: string, variantId: string) =>
    [...broadcastKeys.preview(orgId, broadcastId), variantId] as const,
  /**
   * Family prefix for the who-opened recipient-engagement list (nested under
   * detail). Invalidate THIS prefix so every `engagementList` page / filter /
   * search variant refetches together.
   */
  engagement: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'engagement'] as const,
  /**
   * One page of the recipient-engagement list. Every scope dimension the query
   * uses (filter, search, page) is in the key — omitting one would alias
   * distinct result sets under a single cache entry (frontend.md).
   */
  engagementList: (
    orgId: string | null | undefined,
    broadcastId: string,
    params: {
      readonly filter: BroadcastEngagementFilter;
      readonly search: string;
      readonly page: number;
    },
  ) => [...broadcastKeys.engagement(orgId, broadcastId), params] as const,
  /** Per-link click stats for one broadcast (nested under its detail). */
  linkStats: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'link-stats'] as const,
  /** Hourly opens/clicks time-series buckets for one broadcast (nested under its detail). */
  engagementBuckets: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'engagement-buckets'] as const,
  /**
   * A broadcast's A/B config + lifecycle phase sidecar (`broadcast_ab_tests`),
   * nested under its detail so a `detail`-prefixed invalidation catches it and a
   * phase-transition realtime event can invalidate this family alone.
   */
  abTest: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'ab-test'] as const,
  /** A broadcast's authored A/B arms (`broadcast_variants`), nested under its detail. */
  variants: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'variants'] as const,
  /** Per-arm delivery/engagement counts (`broadcast_variant_stats`), nested under its detail. */
  variantStats: (orgId: string | null | undefined, broadcastId: string) =>
    [...broadcastKeys.detail(orgId, broadcastId), 'variant-stats'] as const,
} as const;
